import { useRef, useState } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import AudioPlayerComponent from "./AudioPlayer";
import { replaceCourseQuestionAudio } from "../service/service";

const getAudioUrl = (question, kind, optionKey, voiceName) => {
  if (voiceName) {
    if (kind === "question") return question.audioUrls?.[voiceName];
    if (kind === "hint") return question.hintAudioUrls?.[voiceName];
    return question.optionAudioUrls?.[optionKey]?.[voiceName];
  }
  if (kind === "question") return question.url;
  if (kind === "hint") return question.hintUrl;
  return question.options?.[optionKey];
};

const VoiceAudioControl = ({
  courseId,
  question,
  kind,
  optionKey,
  voiceName,
  readOnly = false,
  disabled,
  onUpdated,
}) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const actor = voiceName && voiceName !== "__legacy__" ? voiceName : null;
  const audioUrl = getAudioUrl(question, kind, optionKey, actor);
  const label = actor || "Legacy fallback (preview only)";

  const upload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!actor) { setError("Choose a voice actor before uploading."); return; }
    if (
      !/\.mp3$/i.test(file.name) ||
      !/audio\/(mpeg|mp3)/i.test(file.type || "audio/mpeg")
    ) {
      setError("Choose an MP3 file.");
      return;
    }
    setUploading(true);
    setError("");
    const formData = new FormData();
    formData.append("file", file);
    formData.append("scope", "voice");
    formData.append("kind", kind);
    formData.append("voiceName", actor);
    if (optionKey) formData.append("optionKey", optionKey);
    try {
      const result = await replaceCourseQuestionAudio(
        courseId,
        question._id,
        formData,
      );
      onUpdated?.(result.data);
    } catch (uploadError) {
      setError(
        uploadError.response?.data?.err ||
          uploadError.response?.data?.message ||
          "Audio upload failed.",
      );
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box
      sx={{
        maxWidth: 500,
        minWidth: 250,
        flex: "0 1 800px",
        display: "grid",
        gap: 0.75,
        alignItems: "start",
      }}
    >
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      {audioUrl ? (
        <AudioPlayerComponent audioUrl={audioUrl} />
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ my: 1 }}>
          No audio uploaded for this selection.
        </Typography>
      )}
      {!readOnly && actor && (
        <>
          <input
            ref={inputRef}
            type="file"
            accept=".mp3,audio/mpeg"
            hidden
            onChange={upload}
          />
          <Button
            size="small"
            variant="outlined"
            disabled={disabled || uploading}
            onClick={() => inputRef.current?.click()}
          >
            {uploading ? (
              <CircularProgress size={16} />
            ) : audioUrl ? (
              "Replace MP3"
            ) : (
              "Upload MP3"
            )}
          </Button>
        </>
      )}
      {error && (
        <Typography variant="caption" color="error" display="block">
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default VoiceAudioControl;
